<script lang="ts">
	import type { PageProps } from "./$types";
	import { Section, TeamCardDouble, TeamCardSingle } from "#lib/web-ui/primitives/index.js";
	import { cloudinaryUrl, cloudinarySrcset } from "#lib/shared/cloudinary.js";
	import {
		yearsInRealEstate,
		assetsUnderManagement,
		doorsUnderManagement,
		transactionVolume
	} from "#lib/constants/shared-site-config.js";

	let { data }: PageProps = $props();

	const featured = $derived(data.leadership[0]);
	const rest = $derived(data.leadership.slice(1));
</script>

<svelte:head>
	<title>Leadership Team | Fergmar Capital - Real Estate Operators</title>
	<meta
		name="description"
		content="Meet the operators behind Fergmar Capital. {yearsInRealEstate}+ years managing multifamily and commercial real estate. {transactionVolume}+ transaction volume."
	/>
	<meta property="og:title" content="Our Leadership Team | Fergmar Capital" />
	<meta
		property="og:description"
		content="Meet the experienced operators behind Fergmar Capital. {yearsInRealEstate}+ years managing multifamily and commercial real estate investments in Texas."
	/>
	<meta name="twitter:title" content="Our Leadership Team | Fergmar Capital" />
	<meta
		name="twitter:description"
		content="Meet the experienced operators behind Fergmar Capital. {yearsInRealEstate}+ years managing multifamily and commercial real estate investments."
	/>

	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "AboutPage",
		name: "Fergmar Capital Leadership Team",
		description: "Meet the experienced real estate operators behind Fergmar Capital.",
		url: "https://fergmarcapital.com/team",
		mainEntity: {
			"@type": "Organization",
			name: "Fergmar Capital",
			url: "https://fergmarcapital.com",
			founder: {
				"@type": "Person",
				name: "Brian H. Ferguson",
				jobTitle: "Founder & Managing Partner",
				worksFor: { "@type": "Organization", name: "Fergmar Capital" },
				knowsAbout: [
					"Real Estate Syndication",
					"Multifamily Investing",
					"Commercial Real Estate",
					"Property Management",
					"Real Estate Investment Management"
				]
			},
			employee: data.leadership.slice(1).map((m) => ({
				"@type": "Person",
				name: m.displayName,
				jobTitle: m.title,
				...(m.headshot && { image: m.headshot })
			}))
		}
	})}</script>`}
</svelte:head>

<p class="sr-only">
	Fergmar Capital's leadership team brings {yearsInRealEstate}+ years of real estate experience
	across acquisitions, asset management, property management, and investor relations. Led by
	Founder Brian H. Ferguson, the team manages {assetsUnderManagement}+ in assets with over
	{transactionVolume}+ in total transaction volume across multifamily, commercial, and land
	investments across South Texas.
</p>

<Section>
	<div class="mx-auto mb-16 max-w-2xl text-center">
		<p class="text-primary text-[1.17rem] font-bold">Meet the Team</p>
		<h1 class="text-foreground mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
			Fergmar Capital Leadership Team
		</h1>
		<p class="text-muted-foreground mt-6 text-lg text-pretty">
			Fergmar Capital is led by experienced real estate operators with decades of combined
			experience across acquisitions, asset management, and investor relations. We take a
			hands-on, vertically integrated approach to ensure your capital is managed by people who
			understand every facet of the investment lifecycle.
		</p>
	</div>

	{#if featured}
		<TeamCardDouble
			displayName={featured.displayName}
			title={featured.title}
			headshot={featured.headshot}
			label="Founder & Managing Partner"
		>
			<div class="space-y-4">
				{#if featured.bio}
					<p class="text-muted-foreground leading-relaxed">{featured.bio}</p>
				{/if}
				<p class="text-muted-foreground leading-relaxed">
					Today, Brian commands over {assetsUnderManagement} in assets under management across
					{doorsUnderManagement}+ multifamily doors, multiple commercial properties, and
					retail centers, with over {transactionVolume} in total transaction volume over {yearsInRealEstate}+
					years. He started Fergmar Capital because he believes successful business owners
					deserve real estate investments managed by someone who has walked in their
					shoes, not generic investment products.
				</p>
				<p class="text-muted-foreground leading-relaxed">
					In this industry, investors choose the operator, not just the company name.
					Brian's hands-on approach and entrepreneurial background drive strategies built
					around how business owners actually think about wealth building.
				</p>
			</div>
		</TeamCardDouble>
	{/if}

	{#if rest.length > 0}
		<div class="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
			{#each rest as member}
				<TeamCardSingle {...member} />
			{/each}
		</div>
	{/if}
</Section>

<Section theme="muted">
	<div class="mx-auto max-w-4xl text-center">
		<p class="text-primary text-[1.17rem] font-bold">Strength in Numbers</p>
		<h2 class="text-foreground mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
			A Team Built to Perform
		</h2>
		<p class="text-muted-foreground mt-6 text-lg text-pretty">
			Behind every investment is a dedicated team of professionals across acquisitions,
			property management, finance, and operations — working together to protect and grow your
			capital.
		</p>
	</div>
	<div class="mt-12 overflow-hidden rounded-xl">
		<img
			src={cloudinaryUrl("fergmarcapital-com/team-group-photo", { width: 1280 })}
			srcset={cloudinarySrcset("fergmarcapital-com/team-group-photo", 640)}
			sizes="(min-width: 896px) 896px, 100vw"
			alt="The full Fergmar Capital team gathered at company headquarters"
			class="w-full object-cover"
			width="2048"
			height="1365"
			loading="lazy"
		/>
	</div>
</Section>
